import {
  AudioOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  PrinterOutlined,
  RobotOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Button, Divider, Pagination, Segmented, Select, Skeleton, Space, Tag } from "antd";
import { DATE_TIME_FORMAT } from "common/constants";
import { toTitleCase } from "common/utils";
import { CONSENT_FIELDS } from "components/consentTag";
import {
  ConsentDot,
  ConsentItem,
  getConsentColor,
} from "features/debtorSider/components/compactCardUtils";
import { useUserType } from "features/auth";
import { useGetDebtorCommunicationHistoryQuery } from "features/communicationsTable/agencyPortal/communicationsTableAPI";
import NotesModal from "features/communicationsTable/components/notesModal";
import { useGetCreditorPortalDebtorCommunicationHistoryQuery } from "features/communicationsTable/creditorPortal/communicationsTableAPI";
import {
  useFetchDebtorAccountsQuery,
  useFetchDebtorSystemLogsQuery,
} from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import { useFetchCreditorPortalDebtorAccountsQuery } from "features/debtorAccountsTable/creditorPortal/debtorAccountsAPI";
import InlineComposer from "features/debtorProfile/components/inlineComposer";
import { useFetchDebtorPacketedDebtorsQuery } from "features/debtorProfile/debtorProfileAPI";
import { useFetchDebtorContactsQuery } from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { useFetchCreditorPortalDebtorContactsQuery } from "features/debtorSider/creditorPortal/debtorSiderAPI";
import {
  useFetchBackendConstantsQuery,
  useFetchCollectorsQuery,
} from "features/home/agencyPortal/homeAPI";
import { useFetchCommunicationResultItemsQuery } from "features/communicationResultItems/communicationResultItemsAPI";
import { PERMISSIONS, useAuthorization } from "features/permissions";
import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

const StyledMethodIcon = styled.span`
  color: ${({ $color }) => $color};
  margin-top: 3px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
`;

const CONTACT_METHOD_ICON = {
  phone: (
    <StyledMethodIcon $color="#1677ff">
      <PhoneOutlined />
    </StyledMethodIcon>
  ),
  ai_dialer: (
    <StyledMethodIcon $color="#722ed1">
      <RobotOutlined />
    </StyledMethodIcon>
  ),
  text: (
    <StyledMethodIcon $color="#13c2c2">
      <MessageOutlined />
    </StyledMethodIcon>
  ),
  mail: (
    <StyledMethodIcon $color="#fa8c16">
      <MailOutlined />
    </StyledMethodIcon>
  ),
  email: (
    <StyledMethodIcon $color="#52c41a">
      <MailOutlined />
    </StyledMethodIcon>
  ),
  voicemail: (
    <StyledMethodIcon $color="#1677ff">
      <AudioOutlined />
    </StyledMethodIcon>
  ),
  fax: (
    <StyledMethodIcon $color="#595959">
      <PrinterOutlined />
    </StyledMethodIcon>
  ),
};

const getContactMethodIcon = (contactMethod) =>
  CONTACT_METHOD_ICON[contactMethod?.toLowerCase()] ?? (
    <StyledMethodIcon $color="rgba(0,0,0,0.45)">
      <MessageOutlined />
    </StyledMethodIcon>
  );

const FILTER_ALL = "all";
const FILTER_COMMS = "communications";
const FILTER_LOGS = "system_logs";

const StyledFeedWrapper = styled.div`
  margin-top: 4px;
`;

const StyledStickyHeader = styled.div`
  position: sticky;
  top: 64px;
  z-index: 9;
  background: #fff;
  padding: 8px 0;
  margin-bottom: 8px;
`;

const StyledFeedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  max-height: 600px;
  overflow-y: auto;
`;

const StyledCommItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const StyledSysLogItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
`;

const StyledNoteText = styled.div`
  margin-top: 4px;
  white-space: pre-wrap;
  color: rgba(0, 0, 0, 0.88);
  line-height: 1.5;
  font-size: 13px;
`;

const StyledMeta = styled.div`
  margin-top: 3px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`;

const StyledSeeDetails = styled(Button)`
  padding: 0;
  height: auto;
  font-size: 13px;
`;

const StyledInitiatorLine = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-top: 2px;
`;

const StyledCommBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledCommTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const StyledTag = styled(Tag)`
  margin-right: 0;
`;

const StyledRecipient = styled.span`
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
`;

const StyledTimestamp = styled.span`
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const StyledSysLogIcon = styled(SettingOutlined)`
  color: rgba(0, 0, 0, 0.25);
  flex-shrink: 0;
`;

const StyledSysLogText = styled.span`
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledSysLogEmail = styled.strong`
  color: rgba(0, 0, 0, 0.65);
`;

const StyledSysLogTimestamp = styled.span`
  color: rgba(0, 0, 0, 0.35);
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const StyledPagination = styled(Pagination)`
  margin-top: 12px;
  text-align: right;
`;

const StyledConsentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 3px;
`;

const StyledEmptyState = styled.div`
  padding: 24px 0;
  text-align: center;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
`;

const StyledFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

const StyledFilterLabel = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
`;

const StyledFilterDivider = styled(Divider)`
  margin: 12px 0;
`;

const StyledFilterSelect = styled(Select)`
  min-width: 160px;
`;

function UnifiedActivityFeed({
  debtorId,
  hasConsumerConsent = false,
  isDebtorPacketed = false,
  alertNode = null,
}) {
  const { isAgencyUserType, isCreditorUserType } = useUserType();
  const [activeFilter, setActiveFilter] = useState(FILTER_COMMS);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedContactMethods, setSelectedContactMethods] = useState([]);
  const [selectedRecordedBy, setSelectedRecordedBy] = useState([]);

  // Permissions
  const { data: isCreateCommunicationAuthorized } = useAuthorization(
    PERMISSIONS.COMMUNICATION__CREATE,
  );
  const { data: isCommunicationGetNotesAuthorized } = useAuthorization(
    PERMISSIONS.COMMUNICATION__GET_NOTES,
  );
  const { data: isSystemLogAuthorized } = useAuthorization(PERMISSIONS.SYSTEM_LOG__GET);

  // Reset to "All Activity" if the user loses system log permission while on that tab
  useEffect(() => {
    if (!isSystemLogAuthorized && activeFilter === FILTER_LOGS) {
      setActiveFilter(FILTER_ALL);
    }
  }, [isSystemLogAuthorized, activeFilter]);

  // Reset to page 1 when any filter changes
  useEffect(() => {
    setPage(1);
  }, [activeFilter, selectedContactMethods, selectedRecordedBy]);

  // Communications data
  const { data: agencyCommsData, isLoading: isAgencyCommsLoading } =
    useGetDebtorCommunicationHistoryQuery(
      { debtorId, isIncludePacketed: true },
      { skip: !isAgencyUserType },
    );
  const { data: creditorCommsData, isLoading: isCreditorCommsLoading } =
    useGetCreditorPortalDebtorCommunicationHistoryQuery({ debtorId }, { skip: isAgencyUserType });
  const [communicationsData, isCommsLoading] = isAgencyUserType
    ? [agencyCommsData, isAgencyCommsLoading]
    : [creditorCommsData, isCreditorCommsLoading];

  // System logs
  const { data: systemLogs, isLoading: isSystemLogsLoading } = useFetchDebtorSystemLogsQuery(
    { debtorId },
    { skip: !isSystemLogAuthorized },
  );

  // Supporting data
  const { data: agencyDebtorContacts } = useFetchDebtorContactsQuery(
    { debtorId },
    { skip: !isAgencyUserType },
  );
  const { data: creditorPortalDebtorContacts } = useFetchCreditorPortalDebtorContactsQuery(
    { debtorId },
    { skip: isAgencyUserType },
  );
  const debtorContacts = isAgencyUserType ? agencyDebtorContacts : creditorPortalDebtorContacts;

  const { data: agencyDebtorAccounts } = useFetchDebtorAccountsQuery(
    { debtorId },
    { skip: !isAgencyUserType },
  );
  const { data: creditorPortalDebtorAccounts } = useFetchCreditorPortalDebtorAccountsQuery(
    { debtorId },
    { skip: isAgencyUserType },
  );
  const debtorAccounts = isAgencyUserType ? agencyDebtorAccounts : creditorPortalDebtorAccounts;

  const { data: debtorPacketedDebtors } = useFetchDebtorPacketedDebtorsQuery(
    { debtorId },
    { skip: !isAgencyUserType || !isDebtorPacketed },
  );

  const { data: collectors } = useFetchCollectorsQuery({}, { skip: !isAgencyUserType });
  const { data: constants } = useFetchBackendConstantsQuery();
  const { data: allResultItems = [] } = useFetchCommunicationResultItemsQuery({});

  // Lookup maps
  const accountIdToExternalIdMap = useMemo(() => {
    if (!debtorAccounts) return {};
    return debtorAccounts.reduce((acc, account) => {
      acc[account.id] = account.externalId;
      return acc;
    }, {});
  }, [debtorAccounts]);

  const debtorAccountIds = useMemo(
    () => new Set(debtorAccounts?.map((a) => a.id)),
    [debtorAccounts],
  );

  const resultSlugToNameMap = useMemo(
    () =>
      allResultItems.reduce((acc, item) => {
        acc[item.slug] = item.name;
        return acc;
      }, {}),
    [allResultItems],
  );

  const contactMethodToDisplayMap = useMemo(() => {
    if (!constants?.contactMethods) return {};
    return constants.contactMethods.reduce((acc, cm) => {
      acc[cm.value] = cm.display;
      return acc;
    }, {});
  }, [constants]);

  const contactMethodFilterOptions = useMemo(() => {
    const methods = new Set((communicationsData ?? []).map((c) => c.contactMethod).filter(Boolean));
    return [...methods].map((method) => ({
      label: contactMethodToDisplayMap[method] || method,
      value: method,
    }));
  }, [communicationsData, contactMethodToDisplayMap]);

  const recordedByFilterOptions = useMemo(() => {
    const emails = new Set();
    (communicationsData ?? []).forEach((comm) => {
      const email = collectors?.find((c) => c.id === comm.contactedBy)?.email;
      if (email) emails.add(email);
    });
    (systemLogs ?? []).forEach((log) => {
      if (log.userEmail) emails.add(log.userEmail);
    });
    return [...emails].map((email) => ({ label: email, value: email }));
  }, [communicationsData, systemLogs, collectors]);

  // Merge and sort items
  const mergedItems = useMemo(() => {
    const commItems = (communicationsData ?? []).map((comm) => ({
      type: "communication",
      sortTime: comm.communicationStartTime || comm.createdAt,
      data: comm,
      key: `comm-${comm.id}`,
    }));
    const logItems = isSystemLogAuthorized
      ? (systemLogs ?? []).map((log) => ({
          type: "system_log",
          sortTime: log.createdAt,
          data: log,
          key: `log-${log.id}`,
        }))
      : [];
    return [...commItems, ...logItems].sort(
      (a, b) => moment(b.sortTime).unix() - moment(a.sortTime).unix(),
    );
  }, [communicationsData, systemLogs, isSystemLogAuthorized]);

  const filteredItems = useMemo(() => {
    let items = mergedItems;

    if (activeFilter === FILTER_COMMS) items = items.filter((i) => i.type === "communication");
    else if (activeFilter === FILTER_LOGS) items = items.filter((i) => i.type === "system_log");

    if (selectedContactMethods.length > 0) {
      items = items.filter(
        (i) => i.type === "system_log" || selectedContactMethods.includes(i.data.contactMethod),
      );
    }

    if (selectedRecordedBy.length > 0) {
      items = items.filter((i) => {
        if (i.type === "communication") {
          const email = collectors?.find((c) => c.id === i.data.contactedBy)?.email;
          return selectedRecordedBy.includes(email);
        }
        return selectedRecordedBy.includes(i.data.userEmail);
      });
    }

    return items;
  }, [mergedItems, activeFilter, selectedContactMethods, selectedRecordedBy, collectors]);

  const paginatedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, page]);

  const isLoading = isCommsLoading || isSystemLogsLoading;

  const renderRecipient = (comm) => {
    const isPacketed = comm.debtor.toString() !== debtorId.toString();
    if (!isPacketed) {
      if (comm.contactType === "debtor_contact") {
        const contact = debtorContacts?.find((c) => c.id === comm.debtorContact);
        if (contact) {
          const fullName = [contact.firstName, contact.middleName, contact.lastName]
            .filter(Boolean)
            .join(" ");
          return `${fullName} (${contact.relationshipToDebtor})`;
        }
      }
      return toTitleCase({ str: comm.contactType, delimiter: "_" });
    }
    const packetedDebtor = debtorPacketedDebtors?.results?.find(
      (d) => d.id.toString() === comm.debtor.toString(),
    );
    if (!packetedDebtor) return null;
    return (
      <>
        {packetedDebtor.name} <Tag color="blue">PACKETED</Tag>
      </>
    );
  };

  const renderCommItem = (item) => {
    const comm = item.data;
    const cm = comm.contactMethod;
    const cmDisplay = contactMethodToDisplayMap[cm] || cm;
    const hasPhone =
      ["phone", "text", "voicemail", "ai_dialer"].includes(cm?.toLowerCase()) && comm.phoneNumber;
    const cmLabel = hasPhone ? `${cmDisplay} (${comm.phoneNumber})` : cmDisplay;
    const originLabel = comm.communicationOrigin
      ? ` · ${toTitleCase({ str: comm.communicationOrigin })}`
      : "";

    const allResults = [
      ...(comm.communicationResults ?? []).map((r) => ({ slug: r, color: "#2db7f5" })),
      ...(comm.mailResults ?? []).map((r) => ({ slug: r, color: "#ebb25e" })),
      ...(comm.callResults ?? []).map((r) => ({ slug: r, color: "#2fba5b" })),
      ...(comm.voicemailResults ?? []).map((r) => ({ slug: r, color: "#2fba5b" })),
      ...(comm.textResults ?? []).map((r) => ({ slug: r, color: "#2abcdc" })),
    ];

    const affectedAccounts = (comm.accountIds ?? [])
      .map((id) => accountIdToExternalIdMap[id])
      .filter(Boolean);

    const recordedBy = collectors?.find((c) => c.id === comm.contactedBy)?.email;

    const hasRecording = comm.callRecordingUrl && comm.callRecordingUrl !== "";
    const hasTranscription =
      comm.callTranscription &&
      Array.isArray(comm.callTranscription) &&
      comm.callTranscription.length > 0;
    const isTruncated = comm.notes?.length > 200 || hasTranscription || hasRecording;

    const timestamp = comm.communicationStartTime || comm.createdAt;

    return (
      <StyledCommItem key={item.key}>
        {getContactMethodIcon(comm.contactMethod)}
        <StyledCommBody>
          <StyledCommTopRow>
            <Space size={4} wrap>
              <StyledTag>
                {cmLabel}
                {originLabel}
              </StyledTag>
              <StyledRecipient>{renderRecipient(comm)}</StyledRecipient>
              {allResults.map(({ slug, color }, i) => (
                <StyledTag color={color} key={`${slug}-${i}`}>
                  {resultSlugToNameMap[slug] || slug}
                </StyledTag>
              ))}
              {!!comm.isCeaseCommunication && (
                <StyledTag color="red">Do not call anymore</StyledTag>
              )}
            </Space>
            <StyledTimestamp>
              {timestamp ? moment(timestamp).format(DATE_TIME_FORMAT) : "-"}
            </StyledTimestamp>
          </StyledCommTopRow>
          {recordedBy && <StyledInitiatorLine>By: {recordedBy}</StyledInitiatorLine>}
          {CONSENT_FIELDS.some((f) => comm[f.name] != null) && (
            <StyledConsentRow>
              {CONSENT_FIELDS.map((field) => {
                const val = comm[field.name];
                if (val == null) return null;
                const label = val
                  ? `Consent for ${field.tagLabel}`
                  : `No Consent for ${field.tagLabel}`;
                return (
                  <ConsentItem key={field.name}>
                    <ConsentDot $color={getConsentColor(val)} />
                    {label}
                  </ConsentItem>
                );
              })}
            </StyledConsentRow>
          )}
          {isCommunicationGetNotesAuthorized && comm.notes && (
            <StyledNoteText>
              {isTruncated ? (
                <>
                  {comm.notes.substring(0, 200)}...{" "}
                  <StyledSeeDetails type="link" onClick={() => setSelectedRecord(comm)}>
                    See details
                  </StyledSeeDetails>
                </>
              ) : (
                comm.notes
              )}
            </StyledNoteText>
          )}
          <StyledMeta>
            {affectedAccounts.length > 0 && <span>Accounts: {affectedAccounts.join(", ")}</span>}
            {affectedAccounts.length > 0 && recordedBy && <span> · </span>}
            {recordedBy && <span>Recorded by {recordedBy}</span>}
          </StyledMeta>
        </StyledCommBody>
      </StyledCommItem>
    );
  };

  const renderSysLogItem = (item) => {
    const log = item.data;
    const filteredAccounts =
      (log.accountIds ?? []).length === 0
        ? null
        : debtorAccounts?.filter((a) =>
            log.accountIds.filter((id) => debtorAccountIds.has(id)).includes(a.id),
          );

    return (
      <StyledSysLogItem key={item.key}>
        <StyledSysLogIcon />
        <StyledSysLogText>
          {log.userEmail && (
            <>
              <StyledSysLogEmail>{log.userEmail}</StyledSysLogEmail>
              {" · "}
            </>
          )}
          {log.eventType?.display}
          {log.message && ` · ${log.message}`}
          {filteredAccounts?.length > 0 && (
            <> · {filteredAccounts.map((a) => `#${a.externalId}`).join(", ")}</>
          )}
        </StyledSysLogText>
        <StyledSysLogTimestamp>
          {moment(log.createdAt).format(DATE_TIME_FORMAT)}
        </StyledSysLogTimestamp>
      </StyledSysLogItem>
    );
  };

  const filterOptions = [
    { label: "All Activity", value: FILTER_ALL },
    { label: "Communications", value: FILTER_COMMS },
    ...(isSystemLogAuthorized ? [{ label: "System Logs", value: FILTER_LOGS }] : []),
  ];

  return (
    <StyledFeedWrapper>
      {alertNode}
      <StyledStickyHeader>
        <Segmented
          size="small"
          options={filterOptions}
          value={activeFilter}
          onChange={setActiveFilter}
        />
      </StyledStickyHeader>
      {hasConsumerConsent && isCreateCommunicationAuthorized && activeFilter !== FILTER_LOGS && (
        <InlineComposer
          key={activeFilter}
          debtorId={debtorId}
          showModeSelect={activeFilter === FILTER_ALL}
        />
      )}
      {/* Quick note composer for System Logs tab — disabled for now
      {hasConsumerConsent && isCreateCommunicationAuthorized && activeFilter === FILTER_LOGS && (
        <InlineComposer
          key={activeFilter}
          debtorId={debtorId}
          mode="quick_note"
        />
      )}
      */}
      <StyledFilterDivider />
      <StyledFilterRow>
        <StyledFilterLabel>Filter by</StyledFilterLabel>
        <StyledFilterSelect
          mode="multiple"
          allowClear
          size="small"
          placeholder="Contact Method"
          options={contactMethodFilterOptions}
          value={selectedContactMethods}
          onChange={setSelectedContactMethods}
          popupMatchSelectWidth={false}
          disabled={activeFilter === FILTER_LOGS}
        />
        <StyledFilterSelect
          mode="multiple"
          allowClear
          size="small"
          placeholder="Recorded By"
          options={recordedByFilterOptions}
          value={selectedRecordedBy}
          onChange={setSelectedRecordedBy}
          popupMatchSelectWidth={false}
        />
      </StyledFilterRow>
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <StyledFeedList>
          {filteredItems.length === 0 ? (
            <StyledEmptyState>No activity yet</StyledEmptyState>
          ) : (
            paginatedItems.map((item) =>
              item.type === "communication" ? renderCommItem(item) : renderSysLogItem(item),
            )
          )}
        </StyledFeedList>
      )}
      {filteredItems.length > PAGE_SIZE && (
        <StyledPagination
          size="small"
          current={page}
          pageSize={PAGE_SIZE}
          total={filteredItems.length}
          onChange={setPage}
          showSizeChanger={false}
        />
      )}
      <NotesModal
        title="Call Details"
        open={selectedRecord !== null}
        onClose={() => setSelectedRecord(null)}
        record={selectedRecord}
      />
    </StyledFeedWrapper>
  );
}

export default UnifiedActivityFeed;
